import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import VerticalCalendar from '../components/VerticalCalendar';
import DayView from '../components/DayView';
import CreateEventModal from '../components/CreateEventModal';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAILS = ["julie@jcp-teaching.com", "test@example.com", "jc-p@outlook.de"];

const AdminCalendarPage = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calendarView, setCalendarView] = useState('month'); // 'today', 'week', 'month'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'bookings', 'availability', 'events'
  const [viewMode, setViewMode] = useState('month'); // Start with month view
  const [selectedDayView, setSelectedDayView] = useState(new Date().toISOString().split('T')[0]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user && !ADMIN_EMAILS.includes(user.email)) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && ADMIN_EMAILS.includes(user.email) && token) {
      fetchCalendarEvents();
    }
  }, [user, token, currentMonth]);

  const fetchCalendarEvents = async () => {
    if (!token) return;
    setLoading(true);
    try {
      // Get events for 3 months (previous, current, next)
      const today = new Date();
      const startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
      const endDate = new Date(today.getFullYear(), today.getMonth() + 3, 0).toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/admin/calendar`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching calendar:', error);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = (date) => {
    // Switch to day view for the clicked date
    setSelectedDayView(date);
    setViewMode('day');
  };

  const handleCreateAdminEvent = async (eventData) => {
    try {
      await axios.post(`${API}/admin/events`, eventData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event created successfully');
      setShowEventModal(false);
      setSelectedDate(null);
      fetchCalendarEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  if (authLoading || !user) {
    return (
      <div>
        <Navbar />
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <LoadingSkeleton type="card" count={3} />
        </div>
      </div>
    );
  }

  if (!ADMIN_EMAILS.includes(user.email)) {
    return null;
  }

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px', minHeight: '100vh', background: '#fafafa' }}>
        <div className="admin-container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
          {/* Header with back button */}
          <div style={{ 
            background: 'white', 
            padding: '2rem', 
            borderRadius: '12px', 
            marginBottom: '2rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                color: '#7c6f5e',
                cursor: 'pointer',
                fontSize: '0.95rem',
                marginBottom: '1rem',
                padding: '0.5rem',
                borderRadius: '5px',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#f5f5f5'}
              onMouseOut={(e) => e.target.style.background = 'none'}
            >
              <ArrowLeft size={20} />
              Tilbage til Admin Dashboard
            </button>
            <h1 className="booking-title" style={{ marginBottom: '0.5rem', fontSize: '2rem' }}>
              Kalender
            </h1>
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              marginTop: '1rem',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              {/* View mode toggle */}
              {viewMode === 'day' && (
                <button
                  onClick={() => setViewMode('calendar')}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                >
                  ← Tilbage til kalender
                </button>
              )}
              
              {/* Filters - only show in calendar view */}
              {viewMode === 'calendar' && (
                <>
                  <button
                    onClick={() => setActiveFilter('all')}
                    style={{
                      padding: '0.5rem 1rem',
                      background: activeFilter === 'all' ? '#7c6f5e' : 'white',
                      color: activeFilter === 'all' ? 'white' : '#7c6f5e',
                      border: '1px solid #7c6f5e',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}
                  >
                    Alle
                  </button>
                  <button
                    onClick={() => setActiveFilter('bookings')}
                    style={{
                      padding: '0.5rem 1rem',
                      background: activeFilter === 'bookings' ? '#2e7d32' : 'white',
                      color: activeFilter === 'bookings' ? 'white' : '#2e7d32',
                      border: '1px solid #2e7d32',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}
                  >
                    📅 Bookinger
                  </button>
                  <button
                    onClick={() => setActiveFilter('availability')}
                    style={{
                      padding: '0.5rem 1rem',
                      background: activeFilter === 'availability' ? '#f57c00' : 'white',
                      color: activeFilter === 'availability' ? 'white' : '#f57c00',
                      border: '1px solid #f57c00',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}
                  >
                    ⏰ Tilgængelighed
                  </button>
                  <button
                    onClick={() => setActiveFilter('events')}
                    style={{
                      padding: '0.5rem 1rem',
                      background: activeFilter === 'events' ? '#1976d2' : 'white',
                      color: activeFilter === 'events' ? 'white' : '#1976d2',
                      border: '1px solid #1976d2',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}
                  >
                    🎯 Begivenheder
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Calendar Content */}
          <div 
            className="calendar-content"
            style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              minHeight: '400px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h2 style={{ fontSize: '1.5rem', margin: 0 }}>
                {viewMode === 'day' ? 'Dagsvisning' : calendarView === 'month' ? 'Månedsvisning' : 'Kalender'}
              </h2>
              
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* View selector */}
                <select
                  className="calendar-view-selector"
                  value={calendarView}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCalendarView(value);
                    if (value === 'today') {
                      const today = new Date().toISOString().split('T')[0];
                      setSelectedDayView(today);
                      setViewMode('day');
                    } else if (value === 'week') {
                      setViewMode('week');
                    } else if (value === 'month') {
                      setViewMode('month');
                      setCurrentMonth(new Date());
                    }
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #7c6f5e',
                    borderRadius: '5px',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    background: 'white',
                    color: '#333',
                    fontWeight: 500,
                    minWidth: '150px'
                  }}
                >
                  <option value="today">📅 I dag</option>
                  <option value="week">📅 1 Uge</option>
                  <option value="month">📅 1 Måned</option>
                </select>
                
                {/* Month navigation arrows - only show in month view */}
                {calendarView === 'month' && viewMode === 'month' && (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      onClick={() => {
                        const newMonth = new Date(currentMonth);
                        newMonth.setMonth(newMonth.getMonth() - 1);
                        setCurrentMonth(newMonth);
                      }}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: '#7c6f5e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      ←
                    </button>
                    <span style={{ fontSize: '0.95rem', fontWeight: 500, minWidth: '120px', textAlign: 'center' }}>
                      {currentMonth.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' })}
                    </span>
                    <button
                      onClick={() => {
                        const newMonth = new Date(currentMonth);
                        newMonth.setMonth(newMonth.getMonth() + 1);
                        setCurrentMonth(newMonth);
                      }}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: '#7c6f5e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <LoadingSkeleton type="calendar" />
            ) : viewMode === 'day' ? (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <button
                    onClick={() => {
                      // Convert string date to Date object for CreateEventModal
                      const dateStr = selectedDayView || new Date().toISOString().split('T')[0];
                      const dateObj = new Date(dateStr + 'T00:00:00');
                      setSelectedDate(dateObj);
                      setShowEventModal(true);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#1976d2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}
                  >
                    + Opret begivenhed
                  </button>
                </div>
                <DayView
                  date={selectedDayView || new Date().toISOString().split('T')[0]}
                  events={events}
                />
              </>
            ) : viewMode === 'month' ? (
              <VerticalCalendar
                events={events.filter(e => {
                  if (activeFilter === 'all') return true;
                  if (activeFilter === 'bookings') return e.type === 'booking';
                  if (activeFilter === 'availability') return e.type === 'availability';
                  if (activeFilter === 'events') return e.type === 'admin_event';
                  return true;
                })}
                onDateClick={handleDateClick}
                monthsToShow={1}
                startMonth={currentMonth}
              />
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                Vælg en visning fra dropdown
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Event Modal */}
      {showEventModal && selectedDate && (
        <CreateEventModal
          selectedDate={selectedDate}
          onClose={() => {
            setShowEventModal(false);
            setSelectedDate(null);
          }}
          onConfirm={handleCreateAdminEvent}
        />
      )}
    </div>
  );
};

export default AdminCalendarPage;
